import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'

export interface AdminDeliveryRow {
  uuid: string
  odooPickingId: number
  odooPickingName: string
  pickingType: string
  state: string
  scheduledDate: string | null
  effectiveDate: string | null
  deadlineDate: string | null
  carrierName: string | null
  trackingRef: string | null
  deliveryAddress: string | null
  weight: number | null
  synchedAt: string
  orderUuid: string
  quoteReference: string
  sourceCode: string
}

export interface DeliveryCarrier {
  id: number
  name: string
}

export const useDeliveryAdminStore = defineStore('deliveryAdmin', () => {
  const suiteStore = useSuiteStore()

  const deliveries = ref<AdminDeliveryRow[]>([])
  const total = ref(0)
  const carriers = ref<DeliveryCarrier[]>([])
  const isLoading = ref(false)
  const isSyncing = ref(false)
  const isSavingEdit = ref(false)
  const syncResult = ref<{ synced: number; errors: number } | null>(null)

  async function fetchDeliveries(params: {
    limit?: number
    offset?: number
    state?: string
    pickingType?: string
    search?: string
    month?: string // YYYY-MM — si absent, pas de filtre date
  }) {
    isLoading.value = true
    try {
      const q = new URLSearchParams()
      if (params.limit !== undefined) q.set('limit', String(params.limit))
      if (params.offset !== undefined) q.set('offset', String(params.offset))
      if (params.state) q.set('state', params.state)
      if (params.pickingType) q.set('pickingType', params.pickingType)
      if (params.search) q.set('search', params.search)
      if (params.month) q.set('month', params.month)

      const res = await suiteStore.suiteFetch(`orders/deliveries/list?${q}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        const json = await res.json()
        deliveries.value = json.data ?? []
        total.value = json.total ?? 0
      }
    } finally {
      isLoading.value = false
    }
  }

  async function fetchCarriers(sourceCode: string) {
    const res = await suiteStore.suiteFetch(`orders/deliveries/carriers?sourceCode=${encodeURIComponent(sourceCode)}`, {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const json = await res.json()
      carriers.value = json.data ?? []
    }
  }

  async function syncAll(): Promise<{ synced: number; errors: number } | null> {
    isSyncing.value = true
    syncResult.value = null
    try {
      const res = await suiteStore.suiteFetch('orders/delivery/sync-all', {
        method: 'GET',
        credentials: 'include',
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        const json = await res.json()
        syncResult.value = json
        return json
      }
    } finally {
      isSyncing.value = false
    }
    return null
  }

  async function syncOne(orderUuid: string): Promise<boolean> {
    const res = await suiteStore.suiteFetch(`orders/${orderUuid}/delivery/sync`, {
      method: 'POST',
      credentials: 'include',
    })
    return !!(res && typeof res !== 'boolean' && res.ok)
  }

  async function updateDelivery(
    orderUuid: string,
    pickingId: number,
    dto: { carrierId?: number; trackingRef?: string },
  ): Promise<boolean> {
    isSavingEdit.value = true
    try {
      const res = await suiteStore.suiteFetch(`orders/${orderUuid}/delivery/${pickingId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dto),
      })
      return !!(res && typeof res !== 'boolean' && res.ok)
    } finally {
      isSavingEdit.value = false
    }
  }

  return {
    deliveries,
    total,
    carriers,
    isLoading,
    isSyncing,
    isSavingEdit,
    syncResult,
    fetchDeliveries,
    fetchCarriers,
    syncAll,
    syncOne,
    updateDelivery,
  }
})
